import React, { useMemo } from 'react';
import { DateFormat, getDateString } from '../../../../../ui-toolkit/utilities/getDateString';
import { IManualCheckDisbursementData } from '../../../../../types/order/escrow/IManualCheckDisbursementData';
import { IGetUser } from '../../../../../types/users/IUser';
import { downloadEscrowCheck } from '../../shared/downloadEscrowCheck';
import { Table } from '@zillow/constellation';
import { FeeInformation } from '../FeeInformationColumn';
import { getEscrowBankAccountLabel } from 'ui-toolkit/utilities/getEscrowBankAccountLabel';
import { propertyAddressToString } from 'types/address/IPropertyAddressData';
import { prettyManualCheckDisbursementCategory } from 'types/accounting/eManualCheckDisbursementCategory';
import { Button } from '@zillow/constellation';
import { prettyCheckVoidReason } from 'types/accounting/eCheckVoidReason';
import { VoidCheckButton } from 'components/order/fees/edit/disbursements/Voiding/VoidCheckButton';
import { Box } from '@zillow/constellation';
import { Heading } from '@zillow/constellation';
import { currencyDisplay } from 'utilities/shared/currencyDisplay';
import { VoidDisplay } from 'components/order/fees/edit/disbursements/Voiding/VoidDisplay';
import { useGetUsersByQueryQuery } from 'API/UserApi';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

interface IManualCheckDisbursementContainerProps {
  manualCheckDisbursements: IManualCheckDisbursementData[];
}

export const ManualCheckDisbursementsTable: React.FC<IManualCheckDisbursementContainerProps> = ({
  manualCheckDisbursements,
}) => {
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const voidUsers = manualCheckDisbursements
    .filter((t) => !!t.voiderId)
    .map((check) => ({ userId: check.voiderId, userType: 'ADMIN' }) as IGetUser);
  const { data: users = [] } = useGetUsersByQueryQuery(voidUsers);
  const data = useMemo(
    () =>
      manualCheckDisbursements.map((check) => {
        if (check.voiderId) {
          const voidedByUser = users.find((user) => user.id === check.voiderId);
          return { ...check, voidedBy: voidedByUser?.name };
        } else {
          return check;
        }
      }),
    [manualCheckDisbursements, users]
  );

  return (
    <Box>
      <Heading level={1}>Manual Check Disbursements</Heading>
      <Table aria-label="Manual Check Disbursements" size={'sm'} appearance={'zebra'}>
        <Table.Header appearance={'dark'}>
          <Table.Row>
            <Table.HeaderCell>Fee Information</Table.HeaderCell>
            <Table.HeaderCell>Check #</Table.HeaderCell>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Address</Table.HeaderCell>
            <Table.HeaderCell>Spruce Account Label</Table.HeaderCell>
            <Table.HeaderCell>Spruce Account</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>Memo</Table.HeaderCell>
            <Table.HeaderCell>Category</Table.HeaderCell>
            <Table.HeaderCell>Sent At</Table.HeaderCell>
            <Table.HeaderCell>Completed At</Table.HeaderCell>
            <Table.HeaderCell>Check PDF</Table.HeaderCell>
            <Table.HeaderCell>Voided At</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell>
                <FeeInformation id={item.id} />
              </Table.Cell>
              <Table.Cell>{item.checkNumber}</Table.Cell>
              <Table.Cell>{item.name}</Table.Cell>
              <Table.Cell>{item.mailingAddress && propertyAddressToString(item.mailingAddress)}</Table.Cell>
              <Table.Cell>
                {item.escrowBankAccount && getEscrowBankAccountLabel(item.escrowBankAccount, isSpruceWithZillowEnabled, isZillowClosingEnabled)}
              </Table.Cell>
              <Table.Cell>{item.escrowBankAccount.accountNumber}</Table.Cell>
              <Table.Cell>{currencyDisplay(item.amountCents, true)}</Table.Cell>
              <Table.Cell>{item.memo}</Table.Cell>
              <Table.Cell>{prettyManualCheckDisbursementCategory[item.category]}</Table.Cell>
              <Table.Cell>{item.sentAtDate && getDateString(DateFormat.hugeDate, item.sentAtDate)}</Table.Cell>
              <Table.Cell>
                {item.completedAtDate && getDateString(DateFormat.hugeDate, item.completedAtDate)}
              </Table.Cell>
              <Table.Cell>
                <Button onClick={() => downloadEscrowCheck()(item.id)}>View</Button>
              </Table.Cell>
              <Table.Cell>
                <VoidDisplay item={item} prettyVoidReason={item.voidReason && prettyCheckVoidReason[item.voidReason]} />
                <VoidCheckButton checkDisbursement={item} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};
